import { fetchAll, httpGET, httpDELETE, json, getCsrfHeader } from './utils.js';
import { ACTIONS } from '../const';
import { getMembershipForSection } from './membership';
import { updateSelections, fetchUnansweredCount } from './markup';
import {
    getWordData,
    hasSharingStudents,
    selectContent,
    selectWordsURL,
} from '../selectors/content';
import { selectLastPageRead } from '../selectors/membership';
import R from 'ramda';
import { createAction } from 'redux-actions';

const CONTENT_ROOT = '/api/v4/content/';

function mergeManifest(content, manifest) {
    return {
        ...content,
        TOC: manifest.TOC,
        image_base: manifest.image_base,
        words_base: manifest.words_base,
        // This used to get hacked in on the server, now it gets
        // added on the client.
        sizes: manifest.sizes.concat(['thumbs']),
        pages: manifest.pages,
    };
}

const addContent = c => async dispatch => {
    let content = c;
    if (content.media_type == 'text' && !content.manifest_url) {
        throw new Error('Missing manifest_url');
    }

    const cover_override = content.link_cover_override;
    if (cover_override != null) {
        if (cover_override.split(':')[0] === 'vimeo') {
            const [protocol, empty, vimeoId] = cover_override.split('/');
            void (protocol, empty);

            const url = `https://vimeo.com/api/v2/video/${vimeoId}.json`;

            const options = {
                // # this cross site request can't use credentials
                credentials: undefined,
            };
            const link_cover_override = await httpGET(url, options)
                .then(json)
                .then(([item]) => item.thumbnail_large);

            // # fetch will find the real https?: URL and change
            // # link_cover_override
            content = {
                ...content,
                link_cover_override,
            };
        }
    }

    if (content.media_type == 'text' && content.is_finalized) {
        const response = await fetch(content.manifest_url);
        try {
            const manifest = await response.json();
            content = mergeManifest(content, manifest);
        } catch (err) {
            /* eslint-disable no-console */
            console.error(err);
            /* eslint-enable no-console */
        }
    }

    dispatch({
        type: ACTIONS.ADD_CONTENT,
        manifest: content,
    });
};

export const zoomInto = (content, scale, scaleCenter = null) => {
    return {
        type: ACTIONS.SET_CONTENT_SETTINGS,
        content,
        settings: {
            scale,
            scaleCenter,
        },
    };
};

export const setZoom = (content, scale) => {
    return zoomInto(content, scale, null);
};

export const fetchContentForSection = section => dispatch => {
    return fetchAll(
        CONTENT_ROOT + `?section=${section.id}&limit=9000`,
        data => {
            Promise.all(data.objects.map(c => dispatch(addContent(c))));
        }
    );
};

export const fetchSharing = content => (dispatch, getState) => {
    const { section_id, slug } = content;

    if (!hasSharingStudents(getState(), content)) {
        httpGET('/api/v3/note/sharing/')
            .then(json)
            .then(data => {
                dispatch({
                    type: ACTIONS.SET_SHARING_STUDENTS,
                    data,
                });
            });
    }

    const url = `/api/markup/sharing/settings/${section_id}/${slug}/`;
    return httpGET(url)
        .then(json)
        .then(({ with_all_instructor, with_all_students, shared_with }) => {
            dispatch({
                type: ACTIONS.SET_CONTENT_SHARING,
                content,
                with_all_instructor,
                with_all_students,
                shared_with,
            });
        });
};

export const saveSharing = (content, options) => dispatch => {
    const { section_id, slug } = content;
    const { with_all_instructor, with_all_students, shared_with } = options;

    // This mirrors a $.ajax({traditional: true, ...}) form sumission.
    // Normally when you have multiple values for the same key you would
    // append [] to the end of the key. foo: [ 1, 2, 3 ]
    // normal: foo[]=1&foo[]=3&foo[]=3
    // traditional: foo=1&foo=2&foo=3
    let body = `with_all_students=${!!with_all_students}`;
    body += `&with_all_instructor=${!!with_all_instructor}`;
    body = shared_with.reduce(
        (body, value) => `${body}&shared_with=${encodeURIComponent(value)}`,
        body
    );

    options = {
        credentials: 'include',
        method: 'POST',
        headers: {
            ...getCsrfHeader(),
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
    };

    const url = `/api/markup/sharing/settings/${section_id}/${slug}/`;
    // I can't use httpPOST because it assumes we're going to POST JSON
    return fetch(url, options)
        .then(json)
        .then(({ with_all_instructor, with_all_students, shared_with }) => {
            dispatch({
                type: ACTIONS.SET_CONTENT_SHARING,
                content,
                with_all_instructor,
                with_all_students,
                shared_with,
            });
        });
};

export const setShowOthersHighlights = (
    content,
    showOthersHighlights
) => dispatch => {
    dispatch({
        type: ACTIONS.SET_CONTENT_SETTINGS,
        content,
        settings: {
            showOthersHighlights,
        },
    });
};

export const deleteContent = content => dispatch => {
    dispatch({
        type: ACTIONS.DELETE_CONTENT,
        payload: { content },
    });
    return httpDELETE(content.resource_uri);
};

export const refreshContent = id => (dispatch, getState) => {
    return httpGET(`${CONTENT_ROOT}${id}/`)
        .then(json)
        .then(manifest => {
            return Promise.all([
                dispatch(
                    fetchUnansweredCount({
                        ...manifest,
                        section_id: /section\/(\d+)/.exec(manifest.section)[1],
                    })
                ),
                dispatch(addContent(manifest)),
            ]);
        })
        .then(() => {
            return selectContent(getState(), id);
        });
};

export const getContent = id => (dispatch, getState) => {
    const content = selectContent(getState(), id);
    if (content != null) {
        return Promise.resolve(content);
    }

    return dispatch(refreshContent(id));
};

export const getLastPageRead = content => (dispatch, getState) => {
    const { section_id, media_type } = content;
    if (media_type != 'text') {
        throw new Error('Wrong media_type');
    }

    return dispatch(getMembershipForSection(section_id)).then(membership => {
        const ref = selectLastPageRead(getState(), content.id);
        return ref != null ? ref : R.head(content.pages).pagenum;
    });
};

export const getLineData = (contentId, pagenum) => (dispatch, getState) => {
    if (pagenum === null) {
        throw new Error('missing pagenum');
    }

    return dispatch(getContent(contentId)).then(content => {
        const wordData = getWordData(getState(), content, pagenum);
        if (wordData.words) {
            return wordData;
        }

        const uri = selectWordsURL(getState(), content, pagenum);
        httpGET(uri)
            .then(json)
            .then(
                data => {
                    dispatch({
                        type: ACTIONS.ADD_WORD_DATA,
                        uri,
                        data,
                    });
                    dispatch(updateSelections(content, pagenum, data));
                    getWordData(getState(), content, pagenum);
                },
                () => {}
            );
    });
};

export const setOrigin = createAction(ACTIONS.SET_ORIGIN);



// WEBPACK FOOTER //
// ./src/actions/content.js